import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { CommandState } from './serverList'
import { createAgent, getCommand, type PairedServer } from './serverList'
import { onCommand, streamOpen } from './commandStream'
import { FolderField } from './FolderField'
import { createFolderPath } from './browse'
import { relayRead } from '../transport/relay'
import { TmuxGate } from './TmuxGate'
import type { CliStatus } from '../transport/api'
import { CliMissing, InstallExtrasNote } from './CliMissing'
import { sepFor } from './machinePath'
import { onInstallDone } from './cliInstallJobs'
// Split out of StartAgentSheet.tsx in M6 (拆面板, 2026-09-03), moved verbatim —
// the sheet's four blocks were already four functions sharing nothing but the file.
export function CreateAgentSection({
  server,
  onDone,
  onOpenShell,
  showHeading,
  /*
   * The net's interval. 3_000 came from the era when the answer took thirty seconds
   * anyway; the machine finishes in about ninety milliseconds. It also shadowed the
   * `?? 150` below completely — a default silently outranking the fallback beside it,
   * which is why the number I reported and the number that ran were different.
   */
  /*
   * The NET's interval, and it is deliberately not the first ask.
   *
   * 150 ms here was a flood — seven relay requests a second per create, each crossing
   * the router for an answer already on its way, which is how the browser ran out of
   * sockets. But a second is too long to make somebody WAIT for the first answer, and
   * noHeartbeat.test.ts enforces that: a first ask over 200 ms is a number chosen
   * against an era when the answer took thirty seconds. So the two are separated —
   * one prompt ask below, then this as the slow net underneath.
   */
  netMs = 1_000,
}: {
  server: PairedServer
  onDone: (session: string, where: string | null) => void
  /** Open a shell on THIS machine with a command pre-typed — the tmux-install
   *  fallback when the machine needs a password. Wired to the Cmd+J drawer by App. */
  onOpenShell?: (device: string, name: string, command: string) => void
  /** The standalone sheet titles itself; the merged one has a Server field above. */
  showHeading?: boolean
  /** Injectable for tests. */
  netMs?: number | undefined
}) {
  const [session, setSession] = useState('')
  const [cli, setCli] = useState<string>('claude')
  /*
   * WHAT THIS MACHINE CAN ACTUALLY RUN (Rachel, 2026-08-19, via the owner): the
   * type list offered Codex on a Mac with no codex, the create fired, and the
   * answer was a cli_not_found dump — a promise the tap could never keep, same
   * rule as the hidden mic button. /agent/cli_status is asked OF THE CHOSEN
   * DEVICE (relayRead carries a device id; api.get would ask the relay target,
   * which is not necessarily this form's machine).
   *
   * Unknown stays permissive: on error/loading nothing is disabled — a status
   * probe that cannot answer must not lock every type on a machine that could
   * run all of them, and the server's own refusal remains the honest net.
   */
  const cliQ = useQuery({
    queryKey: ['cli-status', server.server_id],
    queryFn: async () => {
      const a = await relayRead('/agent/cli_status', server.server_id)
      return a.status === 200 ? (a.body as CliStatus) : null
    },
    staleTime: 60_000,
    retry: false,
  })
  /** installed / missing / unknown for one select value. */
  const cliState = (value: string): 'in' | 'out' | 'unknown' => {
    const st = cliQ.data
    // The select says 'antigravity'; the wire says 'agy'. The rows live under
    // `clis` — reading them off the top level was the first cut's silent no-op
    // (a stale v1 type, tested green against its own mock).
    const info = st?.clis?.[value === 'antigravity' ? 'agy' : value]
    if (!info || typeof info.installed !== 'boolean') return 'unknown'
    return info.installed ? 'in' : 'out'
  }
  const chosenMissing = cliState(cli) === 'out'
  /*
   * An install that finished while a DIFFERENT type is showing ("Install for me") is still
   * news for the list: that type's "— not installed" goes now, not when it is picked again.
   * The showing type's own box asks the list itself.
   */
  const showing = useRef({ cli, refetch: cliQ.refetch })
  useEffect(() => {
    showing.current = { cli, refetch: cliQ.refetch }
  })
  useEffect(
    () =>
      onInstallDone((device, job) => {
        const shown = showing.current.cli === 'antigravity' ? 'agy' : showing.current.cli
        if (device === server.server_id && job.cli !== shown) void showing.current.refetch()
      }),
    [server.server_id],
  )
  const [cwd, setCwd] = useState('')
  /*
   * Has the person CHOSEN this directory, as opposed to merely being shown where the
   * browser opened?
   *
   * Untouched sends `cwd: null`, which is the contract's "the edge decides where" —
   * and the edge's default is not necessarily the home the browser lands in. Filling
   * the field from the first listing would look identical on screen and quietly move
   * every new agent, so the field reports the browser while this decides what is sent.
   */
  const [cwdTouched, setCwdTouched] = useState(false)
  /** Reported by FolderField, which is the only thing holding the listing. */
  const [existingBase, setExistingBase] = useState('')
  const [missingSegments, setMissingSegments] = useState<string[]>([])
  const [run, setRun] = useState<
    | null
    | { kind: 'rejected'; code: string; detail: string }
    | {
        kind: 'waiting'
        commandId: string
        since: number
        state: 'queued' | 'running'
        /** 0 when the machine is holding a connection; 30 when only the old route is left. */
        collectWithinS: number
      }
    | { kind: 'failed'; error: string }
    | { kind: 'cliMissing' }
    | { kind: 'unknown' }
  >(null)
  const poll = useRef<number | null>(null)
  /** Cancels the stream subscription for the command this sheet is watching. */
  const unsub = useRef<(() => void) | null>(null)
  const [, tick] = useState(0)

  useEffect(
    () => () => {
      if (poll.current !== null) window.clearInterval(poll.current)
    },
    [],
  )

  // tmux rejects these; the BFF answers 400 for them. Checking here too means the
  // button explains itself before a round-trip, but the server stays the authority.
  const badName = /[\s:.]/.test(session)

  const start = async () => {
    /*
     * MAKE THE FOLDER FIRST, when the typed path names one that is not there.
     *
     * The field says so before this runs ("… does not exist — it will be created"), so
     * this is the sentence being kept rather than a surprise. It is one call per
     * missing segment because the machine's /fs/mkdir takes a basename — see
     * createFolderPath, which also explains why "already exists" is the one refusal
     * that gets walked past.
     *
     * A FAILURE HERE STOPS THE CREATE. The alternative — start the agent anyway — is
     * the silent-fallback shape this whole app keeps removing: tmux would launch it in
     * $HOME, report success, and the person would be told their agent is running while
     * looking for it in a directory that was never made.
     */
    /*
     * `cwdTouched` still gates the CREATE, not the send: the field is prefilled with a
     * directory that exists, so an untouched one can never need making — and creating
     * anything from a value the person did not choose is the one thing this must not do.
     */
    if (cwdTouched && missingSegments.length > 0) {
      setRun(null)
      try {
        await createFolderPath(server.server_id, existingBase, missingSegments)
      } catch (e) {
        setRun({
          kind: 'rejected',
          code: 'mkdir_failed',
          detail: `could not create ${missingSegments.join(sepFor(existingBase))} in ${existingBase}: ${
            e instanceof Error ? e.message : String(e)
          }`,
        })
        return
      }
    }
    /*
     * EVERY failure shape lands in `run` — owner, 2026-08-19: 建立 cursor-agent
     * … 連警示標語跳出來都沒有, 整個 app 的狀態像是沒變. createAgent returns
     * {ok:false} for the router's NAMED refusals, but transport-level failures
     * THROW (MachineAway "that machine is not connected", a lost answer's "the
     * machine did not answer", a router 5xx) — and this async function is
     * invoked as `void start()`, so a throw was an unhandled rejection and the
     * screen honestly reported nothing, which reads as nothing happened.
     */
    let r: Awaited<ReturnType<typeof createAgent>>
    try {
      r = await createAgent({
      device_id: server.server_id,
      session: session.trim(),
      cli,
      /*
       * Typed path today; a BROWSER is a filed requirement, not a dropped feature.
       *
       * v1 let you browse the machine's directories when creating an agent, and the
       * owner's correction stands as the rule here: the new app changes the network
       * topology, not the feature set. I had treated "paths never leave the machine"
       * as a reason to delete browsing — but that principle blocks UNREQUESTED
       * upload, and browsing is the account holder asking, same shape as
       * scan/detected. The gap is latency (one beat per directory level is
       * unusable), and the mechanism is homepage's to define — requested via DM,
       * 2026-08-04. Until it lands, this field is the stopgap: empty means "the
       * edge decides", and a typed path reveals nothing its owner does not know.
       */
      /*
       * WHAT THE FIELD SHOWS IS WHAT TRAVELS.
       *
       * This was `cwdTouched ? cwd.trim() || null : null` — so an untouched field sent
       * null no matter what it displayed, and the screen and the request only agreed
       * because the edge's default happened to be the same directory the browse landed
       * in. The owner's point: a default the person can read and edit is the real one;
       * a grey hint is 虛的.
       *
       * `null` — "the edge decides" — is still reachable, by CLEARING the field. That
       * is a choice somebody makes rather than a state they were left in, and it is
       * the one case where the field showing nothing and the request carrying nothing
       * say the same thing.
       */
      cwd: cwd.trim() || null,
      })
    } catch (e) {
      setRun({
        kind: 'rejected',
        code: 'not_sent',
        detail: e instanceof Error ? e.message : String(e),
      })
      return
    }
    if (!r.ok) {
      setRun(
        r.rejection.code === 'cli_not_found'
          ? { kind: 'cliMissing' }
          : { kind: 'rejected', code: r.rejection.code, detail: r.rejection.detail },
      )
      return
    }
    const since = Date.now()
    // Captured before the handler is extracted: inside it, `r` is no longer narrowed.
    const commandId = r.command_id
    const collectWithinS = r.collect_within_s
    setRun({
      kind: 'waiting',
      commandId,
      since,
      state: 'queued',
      collectWithinS,
    })
    /*
     * Be told; ask only while nothing is telling us.
     *
     * The stream carries each state change as the cloud records it. The interval stays
     * underneath as a net — an open socket can still miss a frame, and a sheet that
     * waits forever is the failure this flow already produced once. While the socket is
     * open it does not ask at all.
     */
    unsub.current = onCommand(commandId, (c) => void apply(c))
    /*
     * A NET, NOT A FLOOD — and this line is a fix for a fix.
     *
     * The gate here used to be `if (streamOpen()) return`, which switched the poll off
     * whenever the socket claimed to be OPEN — including when it was half-open and
     * delivering nothing, so a create that finished in a second left the sheet waiting
     * for ever. Removing the gate fixed that and created something worse: an
     * unconditional ask every 150 ms, each one a RELAY REQUEST, with nothing stopping
     * the next tick from firing while the previous was still in flight. Add the
     * post-create fan-out on top and the browser itself ran out of sockets —
     * ERR_INSUFFICIENT_RESOURCES, dozens of them, which is a client-side flood and not
     * a server fault at all.
     *
     * So: one ask at a time (`asking`), a second apart, and terminal states end the
     * loop for good. The stream still wins whenever it is alive — it answers in
     * milliseconds and clears this on the first terminal frame; this only ever gets a
     * turn when the stream has gone quiet, which is exactly what a net is for.
     */
    let asking = false
    let finished = false
    /*
     * ONE prompt ask, then the slow net. The stream normally wins in milliseconds;
     * this covers the case where its frame is already lost by the time we subscribe,
     * without making the person wait a full interval to find out.
     */
    void (async () => {
      try {
        asking = true
        await apply(await getCommand(server.server_id, commandId))
      } catch {
        /* the net below asks again */
      } finally {
        asking = false
      }
    })()
    let netTick = 0
    poll.current = window.setInterval(() => {
      if (asking || finished) return
      /*
       * SLOW while the stream is open, full speed while it is not. The gate here
       * used to be `if (streamOpen()) return` and was removed for the right
       * reason — a half-open socket claims OPEN while delivering nothing, and
       * the sheet waited forever. Skipping four ticks in five keeps that lesson
       * (the net still asks, hard ceiling 5s) while ending the app's only 1Hz
       * network poll in the common case where the push wins in milliseconds.
       * (2026-08-30 trigger audit: this was the ONE setInterval in src.)
       */
      netTick++
      if (streamOpen() && netTick % 5 !== 0) return
      asking = true
      void (async () => {
        try {
          const c = await getCommand(server.server_id, commandId)
          await apply(c)
        } catch {
          // one missed ask is not a failed create; the next tick asks again
        } finally {
          asking = false
        }
      })()
    }, netMs)

    async function apply(c: CommandState) {
        /*
         * ONCE. The stream and the net can both deliver the same terminal state, and
         * `done` calls onCreated, which invalidates the rail and fans out to every
         * machine. Two deliveries meant two fan-outs; the flood above made it more.
         */
        if (finished && c.state !== 'queued' && c.state !== 'running') return
        try {
          if (c.state === 'done') {
            finished = true
            if (poll.current !== null) window.clearInterval(poll.current)
            /*
             * The fallback, said out loud. `requested_cwd` exists only when the
             * session did NOT start where the person asked — the exact case that
             * used to be discovered by ls. It rides the done-path as a sentence
             * rather than blocking anything: the agent is real and running, just
             * somewhere else, and the person deciding what to do about that needs
             * the two paths, not a failure.
             */
            onDone(
              c.result?.session ?? session.trim(),
              c.result?.requested_cwd
                ? `started in ${c.result.cwd ?? 'its default'} — not ${c.result.requested_cwd}, which it could not use`
                : c.result?.cwd
                  ? `started in ${c.result.cwd}`
                  : null,
            )
          } else if (c.state === 'failed') {
            finished = true
            if (poll.current !== null) window.clearInterval(poll.current)
            // The machine SAW it and could not. The error string is the product
            // here — "session name already in use" tells you what to change.
            setRun(
              (c.error ?? '').startsWith('cli_not_found')
                ? { kind: 'cliMissing' }
                : { kind: 'failed', error: c.error ?? 'failed with no reason given' },
            )
          } else if (c.state === 'unknown') {
            finished = true
            if (poll.current !== null) window.clearInterval(poll.current)
            /*
             * Taken, never reported. NOT failed — create is irreversible and the
             * agent may genuinely exist — and rendering it as queued would be this
             * sheet waiting forever for a report that is not coming. "Go look" is
             * the only honest instruction, and Scan above is exactly that look.
             */
            setRun({ kind: 'unknown' })
          } else {
            setRun({
              kind: 'waiting',
              commandId,
              since,
              state: c.state,
              collectWithinS,
            })
            tick((n) => n + 1)
          }
        } catch {
          // A missed poll is not a failed command; the next tick asks again.
        }
    }
  }

  return (
    <>
      {showHeading && <div className="gh-sec">Start a new agent</div>}
      {run?.kind === 'waiting' ? (
        <p className="gh-note">
          {run.state === 'running'
            ? `${server.name} is starting it…`
            : `Starting on ${server.name}… ` +
              (/*
                 * Only a machine we KNOW is not connected gets the reassurance. On a
                 * connected one the answer is milliseconds away, and explaining a wait
                 * that is not happening teaches the user to expect one.
                 */
              run.collectWithinS > 0 && Date.now() - run.since > 5_000
                ? 'That machine is not connected. The command waits for it; nothing is lost.'
                : '')}
        </p>
      ) : (
        <>
          {/* Before anything else: an agent is a tmux session, so a machine without
              tmux cannot start one. The gate is silent when tmux is present. */}
          <TmuxGate
            server={server}
            onOpenShell={(command) => onOpenShell?.(server.server_id, server.name, command)}
          />
          {/*
            Order is the owner's: server (above), folder, name, type, create. The
            browser is INLINE and always showing — the version behind a "Browse…"
            button replaced the whole form, so choosing a directory meant leaving the
            thing you were filling in and coming back to it.
          */}
          <div className="gh-sec">Folder on {server.name}</div>
          {/*
            * ONE LINE, at the owner's request. This was a text field, a path readout,
            * an up-arrow, a create-folder box and a scrolling list — four controls
            * answering one question whose answer was already in the first of them.
            * FolderField makes the text the state and everything else an aid to
            * typing it; see there for the rules.
            */}
          <FolderField
            deviceId={server.server_id}
            serverName={server.name}
            value={cwd}
            onMissing={(base, segs) => {
              setExistingBase(base)
              // Referentially stable only when it changes: setState with an equal array
              // would re-render on every keystroke through the effect that reports it.
              setMissingSegments((prev) =>
                prev.length === segs.length && prev.every((x, i) => x === segs[i]) ? prev : segs,
              )
            }}
            onChange={(p, touched) => {
              setCwd(p)
              /*
               * `touched` still separates "the person chose this" from "we showed them
               * where they are" — untouched sends cwd: null, which is the contract's
               * "the edge decides", and the edge's default is not necessarily the
               * directory a browse happens to open in.
               */
              if (touched) setCwdTouched(true)
            }}
          />

          <div className="gh-sec">Agent name</div>
          <input
            className="nas-input"
            value={session}
            onChange={(e) => setSession(e.target.value)}
            placeholder="session name"
            spellCheck={false}
            autoCapitalize="off"
          />
          {badName && (
            <p className="err-line gh-error">tmux session names cannot contain spaces, “:” or “.”</p>
          )}

          <div className="gh-sec">Agent type</div>
          <select
            className="nas-input nas-select"
            aria-label="Agent type"
            value={cli}
            onChange={(e) => {
              setCli(e.target.value)
              if (run?.kind === 'cliMissing') setRun(null)
            }}
          >
            {/* A type the probe could not find SAYS SO, but is still pickable.
                It used to be disabled, on the assumption that the probe is
                authoritative. It is not: it looks in fewer places than the
                launcher does. Measured 2026-08-20 on spark-244c — claude sat in
                ~/.local/bin, which the LAUNCH path searches and that machine's
                (older) probe did not, so the create would have worked and the
                UI refused to let it be attempted. This file already states the
                rule for a probe that cannot answer ("the server's own refusal
                remains the honest net"); a probe that answers WRONG deserves the
                same treatment. The refusal below is now readable, so letting the
                attempt through no longer costs a PATH dump. */}
            {(
              [
                ['claude', 'Claude'],
                ['cursor', 'Cursor'],
                ['codex', 'Codex'],
                ['antigravity', 'Antigravity'],
                ['grok', 'Grok'],
                ['hermes', 'Hermes'],
                ['muse', 'Muse Code'],
              ] as const
            ).map(([value, label]) => {
              const missing = cliState(value) === 'out'
              return (
                <option key={value} value={value}>
                  {missing ? `${label} — not installed` : label}
                </option>
              )
            })}
          </select>
          {/* Owner, 2026-10-03 (a user's report): a missing CLI must say so, and say how
              to install it. Informs, does not veto (the rule above): Create stays
              pressable, and a create that comes back cli_not_found shows this too. */}
          {(chosenMissing || run?.kind === 'cliMissing') && (
            <CliMissing
              // One box per machine and CLI: an install under way, or a sentence about one,
              // belongs to the machine and CLI it was started for.
              key={`${server.server_id}:${cli}`}
              cli={cli}
              machine={server.name}
              deviceId={server.server_id}
              install={cliQ.data?.clis?.[cli === 'antigravity' ? 'agy' : cli]?.install}
              includes={cliQ.data?.clis?.[cli === 'antigravity' ? 'agy' : cli]?.install_includes}
              checking={cliQ.isFetching}
              onCheckAgain={() => {
                if (run?.kind === 'cliMissing') setRun(null)
                void cliQ.refetch()
              }}
              onInstalled={() => {
                if (run?.kind === 'cliMissing') setRun(null)
                return cliQ.refetch()
              }}
            />
          )}
          {!(chosenMissing || run?.kind === 'cliMissing') && (
            <InstallExtrasNote key={`${server.server_id}:${cli}:extras`} cli={cli} machine={server.name} deviceId={server.server_id} />
          )}

          {/* In sight under a keyboard and a long form (owner, 2026-10-28) — .sheet-stick. */}
          <div className="svr-createrow svr-creatego sheet-stick">
            <button
              type="button"
              className="sheet-btn primary"
              disabled={!session.trim() || badName}
              onClick={() => void start()}
            >
              Create &amp; launch
            </button>

          </div>
          {run?.kind === 'rejected' && (
            <p className="err-line gh-error" role="alert" title={`${run.code}: ${run.detail}`}>
              {/* The machine TRIED and refused — that answer is authoritative, so
                  it carries the weight the pre-flight probe used to. Said in
                  words: `cli_not_found` used to render as the code plus a raw
                  PATH dump (Rachel, 2026-08-19), which is what made a doomed
                  create feel broken rather than explained. The dump is kept on
                  the title attribute, recoverable and not shouted. */}
              {run.code === 'cli_not_found'
                ? `${server.name} tried to start ${cli === 'antigravity' ? 'Antigravity' : cli} and could not find it. Install it there, or link the command somewhere the machine's agent looks (e.g. ~/.local/bin, /usr/local/bin).`
                : `${run.code}: ${run.detail}`}
            </p>
          )}
          {run?.kind === 'failed' && (
            <p className="err-line gh-error" role="alert">
              {server.name} could not start it — {run.error}
            </p>
          )}
          {run?.kind === 'unknown' && (
            <p className="gh-note" role="status">
              {server.name} took the command but has not reported back. The agent may
              or may not exist — use Scan above to see what is actually running.
              Do not start a second one on a guess.
            </p>
          )}
        </>
      )}
    </>
  )
}
